import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, MapPin, CheckCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEvents, useCreateEvent, type EventItem } from '@/hooks/useEvents';
import { Card, Button, Input, SkeletonCard } from '@/components/ui';
import { useT, useLanguage } from '@/i18n/LanguageContext';

export default function EventsPage() {
  const t = useT();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { data: events, isLoading } = useEvents();
  const createEvent = useCreateEvent();
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const setSelectedEventId = useAuthStore((s) => s.setSelectedEventId);
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    checksLimit: '',
    testMode: false,
  });
  const [error, setError] = useState('');

  const handleSelect = (event: EventItem) => {
    setSelectedEventId(event.id);
    navigate('/', { replace: true });
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.startDate || !form.endDate) {
      setError(t.events.fieldsRequired);
      return;
    }

    try {
      const parsedLimit = parseInt(form.checksLimit, 10);
      const event = await createEvent.mutateAsync({
        name: form.name.trim(),
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        location: form.location.trim() || undefined,
        checksLimit: Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : undefined,
        testMode: form.testMode,
      });
      setForm({ name: '', startDate: '', endDate: '', location: '', checksLimit: '', testMode: false });
      setShowForm(false);
      // Auto-select the newly created event
      setSelectedEventId(event.id);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.events.failedCreate);
    }
  };

  const dateLocale = lang === 'zh' ? 'zh-CN' : 'en-US';

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-reevol-text">{t.events.title}</h1>
          <p className="text-sm text-reevol-text-muted">{t.events.subtitle}</p>
        </div>
        {isAdmin && !showForm && (
          <Button onClick={() => setShowForm(true)} icon={<Plus size={16} />}>
            {t.events.newEvent}
          </Button>
        )}
      </div>

      {/* Create event form */}
      {showForm && (
        <Card className="p-5">
          <h2 className="text-sm font-semibold text-reevol-text mb-3">{t.events.createEvent}</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <Input
              label={t.events.eventName}
              placeholder={t.events.eventNamePlaceholder}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              autoFocus
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t.events.startDate}
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                required
              />
              <Input
                label={t.events.endDate}
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                required
              />
            </div>
            <Input
              label={t.events.locationOptional}
              placeholder={t.events.locationPlaceholder}
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            />
            <Input
              label={t.events.checkLimit}
              type="number"
              min={1}
              placeholder="50"
              value={form.checksLimit}
              onChange={(e) => setForm((f) => ({ ...f, checksLimit: e.target.value }))}
            />
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-reevol-border text-reevol-indigo focus:ring-reevol-indigo"
                checked={form.testMode}
                onChange={(e) => setForm((f) => ({ ...f, testMode: e.target.checked }))}
              />
              <span>
                <span className="block text-sm font-medium text-reevol-text">{t.events.testMode}</span>
                <span className="block text-xs text-reevol-text-muted">{t.events.testModeHint}</span>
              </span>
            </label>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
            )}
            <div className="flex gap-2">
              <Button type="submit" loading={createEvent.isPending}>
                {t.events.create}
              </Button>
              <Button variant="secondary" onClick={() => { setShowForm(false); setError(''); }}>
                {t.common.cancel}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && events?.length === 0 && (
        <Card className="p-8 text-center">
          <Calendar size={32} className="mx-auto text-reevol-text-muted mb-3" />
          <p className="text-sm font-medium text-reevol-text mb-1">{t.events.noEventsYet}</p>
          <p className="text-xs text-reevol-text-muted mb-4">{t.events.noEventsDesc}</p>
          {isAdmin && (
            <Button onClick={() => setShowForm(true)} icon={<Plus size={16} />}>
              {t.events.createFirstEvent}
            </Button>
          )}
        </Card>
      )}

      {/* Event list */}
      {events && events.length > 0 && (
        <div className="space-y-3">
          {events.map((event) => (
            <Card
              key={event.id}
              className={`p-5 cursor-pointer transition-all hover:border-reevol-blue/40 hover:shadow-md active:scale-[0.99] ${
                selectedEventId === event.id ? 'ring-2 ring-reevol-blue/30 border-reevol-blue/40' : ''
              }`}
              onClick={() => handleSelect(event)}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-reevol-text truncate">{event.name}</h3>
                    {selectedEventId === event.id && (
                      <CheckCircle size={14} className="text-reevol-blue flex-shrink-0" />
                    )}
                    {event.testMode && (
                      <span className="flex-shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                        {t.events.testBadge}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-reevol-text-muted">
                    <span>{formatDate(event.startDate)} — {formatDate(event.endDate)}</span>
                    {event.location && (
                      <span className="flex items-center gap-0.5">
                        <MapPin size={10} />
                        {event.location}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-sm font-bold text-reevol-text">
                    {event.checksLimit - event.checksUsed}
                  </p>
                  <p className="text-[10px] text-reevol-text-muted">{t.common.checksLeft}</p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-reevol-blue to-reevol-cyan transition-all duration-500"
                  style={{ width: `${Math.min(100, (event.checksUsed / event.checksLimit) * 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-reevol-text-muted mt-1">
                {event.checksUsed} / {event.checksLimit} {t.common.checksUsed}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
